"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { useCart } from '@/context/CartContext';
import { useStore } from '@/context/StoreContext';
import toast, { Toaster } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCartShopping, faThLarge, faArrowRight, faCalendarAlt, faClock, faMotorcycle
} from '@fortawesome/free-solid-svg-icons';
import Link from 'next/link';
import BookingModal from '@/components/BookingModal';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
// @ts-ignore
import 'swiper/css';
// @ts-ignore
import 'swiper/css/navigation';
// @ts-ignore
import 'swiper/css/pagination';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import BenefitsBar from '@/components/BenefitsBar';
import { formatCurrency as formatPriceUtil } from '@/lib/formatters';
import { getSubdomain } from '@/lib/subdomain';
import { getApiBaseUrl } from '@/lib/api';

// --- Interfaces ---
interface ApiCategory { id: number; name: string; slug: string; image_url?: string | null; }
interface ApiVariant { id: number; sale_price: string; quantity: number; color: string | null; size: string | null; promotional_price?: string | null; image_url?: string | null; }
interface ApiProduct { id: number; name: string; image_url: string | null; description: string; category: ApiCategory; isPromotion?: boolean; starting_price: string | number | null; regular_starting_price: string | number | null; variants: ApiVariant[]; total_stock: number; }
interface ApiService { id: number; name: string; image_url: string | null; description: string; category: ApiCategory; sale_price: string; duration_minutes: number | null; }
interface PaginatedProductsResponse { data: ApiProduct[]; current_page: number; last_page: number; total: number; }
interface ApiSection { id: number; name: string; products: ApiProduct[]; banner_url?: string | null; banner_action_url?: string | null; display_layout?: 'grid' | 'carousel' | null; }

const SearchIcon = (props: React.SVGProps<SVGSVGElement>) => (<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>);

const formatCurrency = (value: string | number | null, currencyCode?: string): string => {
    if (value === null || value === undefined) return 'Consulte';
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numericValue)) return 'Consulte';
    return formatPriceUtil(numericValue, currencyCode || 'BRL');
};

const ProductCard = ({ product }: { product: ApiProduct }) => {
    const { addToCart, openCart } = useCart();
    const { settings } = useStore();
    const formatPrice = (v: string | number | null) => formatCurrency(v, settings.currency);
    const template = settings.store_template || 'default';
    const isOnSale = product.regular_starting_price && product.starting_price !== product.regular_starting_price;
    const isOutOfStock = product.total_stock <= 0;
    const priceNum = Number(product.starting_price) || 0;
    const installment12x = priceNum > 0 ? (priceNum / 12) : 0;

    const handleQuickAdd = (e: React.MouseEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (isOutOfStock) return;

        if (product.variants && product.variants.length > 1) {
            window.location.href = `/produtos/${product.id}`;
            return;
        }

        const variant = product.variants?.[0];
        const variantId = variant?.id || product.id;
        const variantPrice = (variant?.promotional_price && parseFloat(variant.promotional_price) > 0)
            ? variant.promotional_price
            : (variant?.sale_price || product.starting_price || '0');

        addToCart({ 
            id: variantId, 
            name: product.name, 
            price: String(variantPrice), 
            imageUrl: variant?.image_url || product.image_url || '', 
            type: 'product_variant', 
            quantity: 1 
        });
        toast.success(`${product.name} adicionado!`, { icon: '🛒', style: { borderRadius: '10px', background: '#333', color: '#fff' } });
    };

    const handleBuyNow = (e: React.MouseEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (isOutOfStock) return;

        if (product.variants && product.variants.length > 1) {
            window.location.href = `/produtos/${product.id}`;
            return;
        }

        const variant = product.variants?.[0];
        const variantId = variant?.id || product.id;
        const variantPrice = (variant?.promotional_price && parseFloat(variant.promotional_price) > 0)
            ? variant.promotional_price
            : (variant?.sale_price || product.starting_price || '0');

        addToCart({ 
            id: variantId, 
            name: product.name, 
            price: String(variantPrice), 
            imageUrl: variant?.image_url || product.image_url || '', 
            type: 'product_variant', 
            quantity: 1 
        });
        openCart();
    };

    if (template === 'fashion') {
        return (
            <Link href={`/produtos/${product.id}`} className="block h-full group">
                <div className="product-card-fashion bg-white rounded-xl overflow-hidden border border-gray-100 shadow-xs hover:shadow-lg transition-all flex flex-col h-full">
                    <div className="relative w-full pt-[130%] bg-gray-50 overflow-hidden">
                        <img 
                            className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${isOutOfStock ? 'grayscale opacity-60' : ''}`}
                            src={product.image_url || 'https://placehold.co/400x520/f7f7f7/cccccc?text=Sem+Foto'} 
                            alt={product.name} 
                        />
                        {isOnSale && !isOutOfStock && (
                            <span className="absolute top-2.5 left-2.5 bg-black text-white text-[10px] font-bold px-2 py-0.5 tracking-widest uppercase">
                                SALE
                            </span>
                        )}
                        {isOutOfStock && (
                            <span className="absolute top-2.5 right-2.5 bg-gray-800 text-white text-[10px] font-medium px-2 py-0.5 tracking-wider uppercase">
                                Esgotado
                            </span>
                        )}
                    </div>
                    <div className="p-3.5 flex flex-col flex-grow text-center">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest truncate mb-1">
                            {product.category?.name || 'Coleção'}
                        </span>
                        <h3 className="font-medium text-gray-900 text-sm leading-tight mb-2 line-clamp-2 min-h-[2.5em] group-hover:text-[var(--primary)] transition-colors">
                            {product.name}
                        </h3>
                        <div className="mt-auto pt-2 border-t border-gray-100 flex items-center justify-between">
                            <div className="text-left">
                                {isOnSale && (
                                    <span className="text-[11px] text-gray-400 line-through block">
                                        {formatPrice(product.regular_starting_price)}
                                    </span>
                                )}
                                <span className="font-semibold text-gray-900 text-base">
                                    {formatPrice(product.starting_price)}
                                </span>
                            </div>
                            {!isOutOfStock && (
                                <button 
                                    onClick={handleQuickAdd} 
                                    className="w-8 h-8 rounded-full border border-gray-200 hover:border-black hover:bg-black hover:text-white text-gray-700 flex items-center justify-center transition-all"
                                    title="Adicionar à sacola"
                                >
                                    <FontAwesomeIcon icon={faCartShopping} className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    if (template === 'electronics') {
        return (
            <Link href={`/produtos/${product.id}`} className="block h-full group">
                <div className="product-card-tech bg-white rounded-2xl overflow-hidden border border-slate-200/90 hover:border-blue-400 hover:shadow-xl transition-all flex flex-col h-full shadow-xs">
                    <div className="relative w-full pt-[85%] bg-slate-50 overflow-hidden flex items-center justify-center p-4">
                        <img 
                            className={`absolute inset-0 w-full h-full object-contain p-4 mix-blend-multiply transition-transform duration-300 group-hover:scale-105 ${isOutOfStock ? 'grayscale opacity-60' : ''}`}
                            src={product.image_url || 'https://placehold.co/400x340/f1f5f9/94a3b8?text=Sem+Foto'} 
                            alt={product.name} 
                        />
                        {isOnSale && !isOutOfStock && (
                            <span className="absolute top-2.5 left-2.5 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider shadow-xs">
                                Oferta Tech
                            </span>
                        )}
                        {isOutOfStock && (
                            <span className="absolute top-2.5 right-2.5 bg-slate-700 text-white text-[10px] font-bold px-2 py-0.5 rounded-md uppercase">
                                Esgotado
                            </span>
                        )}
                    </div>
                    <div className="p-4 flex flex-col flex-grow">
                        <span className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider truncate mb-1">
                            {product.category?.name || 'Tecnologia'}
                        </span>
                        <h3 className="font-semibold text-slate-800 text-sm leading-snug mb-2 line-clamp-2 min-h-[2.5em] group-hover:text-blue-600 transition-colors">
                            {product.name}
                        </h3>
                        <div className="mt-auto pt-2 border-t border-slate-100">
                            {isOutOfStock ? (
                                <p className="text-xs font-bold text-slate-400">Produto indisponível</p>
                            ) : (
                                <div>
                                    {isOnSale && (
                                        <span className="text-xs text-slate-400 line-through block">
                                            {formatPrice(product.regular_starting_price)}
                                        </span>
                                    )}
                                    <div className="flex items-baseline justify-between gap-1">
                                        <span className="font-bold text-slate-900 text-lg">
                                            {formatPrice(product.starting_price)}
                                        </span>
                                        <button 
                                            onClick={handleQuickAdd} 
                                            className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white flex items-center justify-center transition-all"
                                            title="Comprar"
                                        >
                                            <FontAwesomeIcon icon={faCartShopping} className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    {priceNum > 50 && (
                                        <span className="text-[11px] text-emerald-600 font-medium mt-0.5 block">
                                            em até 12x de {formatPrice(installment12x)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    if (template === 'sports_supplements') {
        return (
            <Link href={`/produtos/${product.id}`} className="block h-full group">
                <div className="product-card-sports bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-red-500 hover:shadow-xl transition-all flex flex-col h-full shadow-xs">
                    <div className="relative w-full pt-[95%] bg-gray-50 overflow-hidden">
                        <img 
                            className={`absolute inset-0 w-full h-full object-contain p-3 transition-transform duration-300 group-hover:scale-105 ${isOutOfStock ? 'grayscale opacity-60' : ''}`}
                            src={product.image_url || 'https://placehold.co/400x400/f4f4f5/71717a?text=Sem+Foto'} 
                            alt={product.name} 
                        />
                        {isOnSale && !isOutOfStock && (
                            <span className="absolute top-2.5 left-2.5 bg-red-600 text-white text-[11px] font-black px-2 py-0.5 rounded uppercase tracking-wider">
                                -{Math.round((1 - (Number(product.starting_price)/Number(product.regular_starting_price))) * 100)}% OFF
                            </span>
                        )}
                        {isOutOfStock && (
                            <span className="absolute top-2.5 right-2.5 bg-gray-800 text-white text-[10px] font-black px-2 py-0.5 rounded uppercase">
                                Esgotado
                            </span>
                        )}
                    </div>
                    <div className="p-4 flex flex-col flex-grow bg-white">
                        <span className="text-[10px] font-extrabold text-red-600 uppercase tracking-wider truncate mb-1">
                            {product.category?.name || 'Performance'}
                        </span>
                        <h3 className="font-extrabold uppercase text-gray-900 text-sm leading-tight mb-2 line-clamp-2 min-h-[2.5em] group-hover:text-red-600 transition-colors">
                            {product.name}
                        </h3>
                        <div className="mt-auto pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
                            <div>
                                {isOnSale && (
                                    <span className="text-xs text-gray-400 line-through block">
                                        {formatPrice(product.regular_starting_price)}
                                    </span>
                                )}
                                <span className="font-black text-gray-900 text-lg">
                                    {formatPrice(product.starting_price)}
                                </span>
                            </div>
                            {!isOutOfStock && (
                                <button 
                                    onClick={handleQuickAdd} 
                                    className="bg-red-600 hover:bg-red-700 text-white font-extrabold text-xs px-3.5 py-2 rounded-lg uppercase tracking-wider transition-all flex items-center gap-1.5 active:scale-95 shadow-sm"
                                >
                                    <FontAwesomeIcon icon={faCartShopping} className="w-3 h-3" />
                                    <span>Comprar</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </Link>
        );
    }

    return (
        <Link href={`/produtos/${product.id}`} className="block h-full group">
            <div className="product-card">
                <div className="product-image-container">
                    <img className={`product-image ${isOutOfStock ? 'grayscale opacity-60' : ''}`} src={product.image_url || 'https://placehold.co/400x400/f0f0f0/cccccc?text=Sem+Imagem'} alt={product.name} />
                    {isOnSale && !isOutOfStock && <div className="badge-sale">-{Math.round((1 - (Number(product.starting_price)/Number(product.regular_starting_price))) * 100)}%</div>}
                    {isOutOfStock && <div className="badge-stock">Esgotado</div>}
                </div>
                <div className="p-4 flex flex-col flex-grow">
                    <p className="text-xs text-[var(--text-muted)] mb-1 uppercase tracking-wide truncate">{product.category?.name || 'Geral'}</p>
                    <h3 className="font-semibold text-[var(--text-main)] text-sm md:text-base leading-tight mb-2 line-clamp-2 min-h-[2.5em] group-hover:text-[var(--primary)] transition-colors">{product.name}</h3>
                    <div className="mt-auto pt-2 border-t border-gray-50">
                        {isOutOfStock ? ( <p className="text-sm font-bold text-gray-400">Indisponível</p> ) : (
                            <div className="flex flex-col">
                                {isOnSale && <span className="text-xs text-[var(--text-muted)] line-through">{formatPrice(product.regular_starting_price)}</span>}
                                <div className="flex items-center justify-between mt-1 gap-2">
                                    <span className="font-bold text-[var(--primary)] text-lg truncate">{formatPrice(product.starting_price)}</span>
                                    
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <button onClick={handleBuyNow} className="hidden sm:inline-flex items-center justify-center bg-[var(--primary)] text-white text-xs font-bold px-3.5 py-2 rounded-xl shadow-xs hover:brightness-110 active:scale-95 transition-all">
                                            Comprar
                                        </button>
                                        <button onClick={handleQuickAdd} className="w-8 h-8 rounded-xl bg-gray-100 text-[var(--primary)] flex items-center justify-center hover:bg-gray-200 active:scale-95 transition-all" title="Adicionar ao Carrinho">
                                            <FontAwesomeIcon icon={faCartShopping} className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Link>
    );
};

const FoodProductCard = ({ product }: { product: ApiProduct }) => {
    const { addToCart, openCart } = useCart();
    const { settings } = useStore();
    const formatPrice = (v: string | number | null) => formatCurrency(v, settings.currency);
    const isOnSale = product.regular_starting_price && product.starting_price !== product.regular_starting_price;
    const isOutOfStock = product.total_stock <= 0;

    const handleQuickAdd = (e: React.MouseEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (isOutOfStock) return;

        if (product.variants && product.variants.length > 1) {
            window.location.href = `/produtos/${product.id}`;
            return;
        }

        const variant = product.variants?.[0];
        const variantId = variant?.id || product.id;
        const variantPrice = (variant?.promotional_price && parseFloat(variant.promotional_price) > 0)
            ? variant.promotional_price
            : (variant?.sale_price || product.starting_price || '0');

        addToCart({ 
            id: variantId, 
            name: product.name, 
            price: String(variantPrice), 
            imageUrl: variant?.image_url || product.image_url || '', 
            type: 'product_variant', 
            quantity: 1 
        });
        toast.success(`${product.name} adicionado!`, { icon: '🛒', style: { borderRadius: '10px', background: '#333', color: '#fff' } });
    };

    const handleBuyNow = (e: React.MouseEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (isOutOfStock) return;

        if (product.variants && product.variants.length > 1) {
            window.location.href = `/produtos/${product.id}`;
            return;
        }

        const variant = product.variants?.[0];
        const variantId = variant?.id || product.id;
        const variantPrice = (variant?.promotional_price && parseFloat(variant.promotional_price) > 0)
            ? variant.promotional_price
            : (variant?.sale_price || product.starting_price || '0');

        addToCart({ 
            id: variantId, 
            name: product.name, 
            price: String(variantPrice), 
            imageUrl: variant?.image_url || product.image_url || '', 
            type: 'product_variant', 
            quantity: 1 
        });
        openCart();
    };

    return (
        <Link href={`/produtos/${product.id}`} className="block w-full group">
            <div className={`bg-white rounded-2xl p-3 flex gap-4 border border-gray-100 shadow-sm transition-all hover:shadow-md hover:border-[var(--primary)]/30 ${isOutOfStock ? 'opacity-70' : ''}`}>
                <div className="flex-grow flex flex-col justify-center py-1">
                    <h3 className="text-sm md:text-base font-bold text-gray-800 group-hover:text-[var(--primary)] transition-colors mb-1 line-clamp-2">{product.name}</h3>
                    <p className="text-xs text-gray-500 line-clamp-2 mb-3">{product.description}</p>
                    <div className="flex items-end justify-between mt-auto">
                        <div>
                            {isOutOfStock ? (
                                <span className="text-xs font-bold text-gray-400">Esgotado</span>
                            ) : (
                                <div className="flex items-baseline gap-2">
                                     {isOnSale && <span className="text-xs text-gray-400 line-through">{formatPrice(product.regular_starting_price)}</span>}
                                     <span className="text-base font-bold text-[var(--primary)]">{formatPrice(product.starting_price)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="w-24 h-24 flex-shrink-0 rounded-xl relative overflow-hidden bg-gray-50 shadow-inner">
                    <img className="w-full h-full object-cover" src={product.image_url || 'https://placehold.co/400x400/f0f0f0/cccccc?text=Sem+Imagem'} alt={product.name} />
                    {isOnSale && !isOutOfStock && <div className="absolute top-0 right-0 bg-red-600 w-full text-center text-white text-[9px] font-bold py-0.5">OFERTA</div>}
                    {!isOutOfStock && (
                        <div className="absolute bottom-1 right-1 flex gap-1">
                            <button onClick={handleBuyNow} className="hidden sm:block w-7 h-7 rounded-full bg-[var(--primary)] text-white flex items-center justify-center shadow-md hover:scale-110 active:scale-95 transition-all" title="Comprar Agora">
                                <FontAwesomeIcon icon={faCartShopping} className="w-3 h-3" />
                            </button>
                            <button onClick={handleQuickAdd} className="w-7 h-7 rounded-full bg-gray-100 text-[var(--primary)] flex items-center justify-center shadow-md hover:scale-110 active:scale-95 transition-all" title="Adicionar ao Carrinho">
                                <FontAwesomeIcon icon={faCartShopping} className="w-3 h-3" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
};

const ProductSkeleton = () => (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-100 animate-pulse">
        <div className="pt-[100%] bg-gray-200"></div>
        <div className="p-4 space-y-2"><div className="h-3 bg-gray-200 rounded w-1/3"></div><div className="h-4 bg-gray-200 rounded w-3/4"></div><div className="h-6 bg-gray-200 rounded w-1/2 mt-4"></div></div>
    </div>
);

export default function HomePage() {
    const { settings, isLoading: isLoadingSettings } = useStore();
    
    const [products, setProducts] = useState<ApiProduct[]>([]);
    const [sections, setSections] = useState<ApiSection[]>([]);
    const [featuredProducts, setFeaturedProducts] = useState<ApiProduct[]>([]);
    const [services, setServices] = useState<ApiService[]>([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

    const closeBookingModal = () => setIsBookingModalOpen(false);

    const currentTemplate = settings.store_template || 'default';
    const isFoodStyle = settings.catalog_type === 'food' || currentTemplate === 'food';

    const fetchHomeData = useCallback(async () => {
        setIsLoadingData(true);
        try {
            const subdomain = getSubdomain();
            if (!subdomain) return;

            const apiBaseUrl = getApiBaseUrl();
            const apiHeaders = { 'X-Tenant-Subdomain': subdomain, 'Accept': 'application/json' };

            const [sectionsRes, servicesRes, featuredRes] = await Promise.all([
                fetch(`${apiBaseUrl}/public/products-by-section?subdomain=${encodeURIComponent(subdomain)}`, { headers: apiHeaders, cache: 'no-store' }),
                fetch(`${apiBaseUrl}/public/services?subdomain=${encodeURIComponent(subdomain)}`, { headers: apiHeaders, cache: 'no-store' }),
                fetch(`${apiBaseUrl}/public/products/featured?subdomain=${encodeURIComponent(subdomain)}`, { headers: apiHeaders, cache: 'no-store' })
            ]);

            let loadedSections: ApiSection[] = [];
            if (sectionsRes.ok) {
                loadedSections = await sectionsRes.json();
                setSections(loadedSections);
            }
            if (servicesRes.ok) setServices(await servicesRes.json());
            if (featuredRes.ok) setFeaturedProducts(await featuredRes.json());

            // Blindagem Retroativa de Lojas Existentes:
            // Se a loja não possui seções de vitrine cadastradas (sections.length === 0),
            // busca os produtos reais do catálogo geral para garantir que a home page continue exibindo os produtos!
            if (!loadedSections || loadedSections.length === 0) {
                const catalogRes = await fetch(`${apiBaseUrl}/public/products?subdomain=${encodeURIComponent(subdomain)}&per_page=20`, { headers: apiHeaders, cache: 'no-store' });
                if (catalogRes.ok) {
                    const catalogData = await catalogRes.json();
                    const realProducts = Array.isArray(catalogData) ? catalogData : (catalogData.data || []);
                    setProducts(realProducts);
                }
            }
        } catch (error) {
            console.error("Erro:", error);
            toast.error("Erro ao carregar loja.");
        } finally {
            setIsLoadingData(false);
        }
    }, []);

    const fetchPaginatedProducts = useCallback(async (page: number, search: string) => {
        if (!search) return;
        setIsLoadingProducts(true);
        try {
            const subdomain = getSubdomain();
            const apiBaseUrl = getApiBaseUrl();
            const apiHeaders = { 'X-Tenant-Subdomain': subdomain, 'Accept': 'application/json' };

            let productUrl = `${apiBaseUrl}/public/products?subdomain=${encodeURIComponent(subdomain)}&page=${page}&search=${search}`;
            const productsRes = await fetch(productUrl, { headers: apiHeaders });
            if (!productsRes.ok) throw new Error('Falha');

            const productsData: PaginatedProductsResponse = await productsRes.json();
            setProducts(productsData.data);
            setCurrentPage(productsData.current_page);
            setTotalPages(productsData.last_page);
        } catch {
            toast.error("Erro na busca.");
        } finally {
            setIsLoadingProducts(false);
        }
    }, []);

    useEffect(() => { fetchHomeData(); }, [fetchHomeData]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm) {
                setSections([]);
                fetchPaginatedProducts(1, searchTerm);
            } else {
                fetchHomeData();
                setProducts([]);
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, fetchPaginatedProducts, fetchHomeData]);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            fetchPaginatedProducts(newPage, searchTerm);
            document.getElementById('catalog-start')?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const scrollToCatalog = () => {
        document.getElementById('catalog-start')?.scrollIntoView({ behavior: 'smooth' });
    };

    if (isLoadingSettings) return <div className="h-screen flex items-center justify-center bg-white"><div className="w-10 h-10 border-4 border-gray-200 border-t-orange-500 rounded-full animate-spin"></div></div>;

    const activeHeroBanners = (settings.hero_banners && settings.hero_banners.length > 0 
        ? settings.hero_banners 
        : [{ image_url: settings.banner_url || null, action_url: null }]
    ).filter((b: any) => !!b.image_url);

    return (
        <div className="min-h-screen flex flex-col" data-template={currentTemplate}>
            <style jsx global>{`
              .scrollbar-hide::-webkit-scrollbar { display: none; }
              .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>

            <Header searchValue={searchTerm} onSearchChange={setSearchTerm} />

            {!searchTerm && activeHeroBanners.length > 0 && (
                <section className="w-full mb-6">
                    <Swiper
                        modules={[Navigation, Pagination, Autoplay]}
                        spaceBetween={0}
                        slidesPerView={1}
                        navigation
                        pagination={{ clickable: true }}
                        autoplay={{ delay: 5000, disableOnInteraction: false }}
                        className="w-full h-auto"
                    >
                        {activeHeroBanners.map((banner: any, idx: number) => {
                            return (
                                <SwiperSlide key={idx}>
                                    <div className="relative w-full overflow-hidden">
                                        {banner.action_url ? (
                                            <Link href={banner.action_url} className="block w-full outline-none">
                                                <picture>
                                                    {banner.mobile_image_url && (
                                                        <source media="(max-width: 768px)" srcSet={banner.mobile_image_url} />
                                                    )}
                                                    <img 
                                                        src={banner.image_url} 
                                                        alt={`Banner ${idx + 1}`} 
                                                        className="w-full h-[360px] sm:h-[440px] md:h-[500px] object-cover"
                                                    />
                                                </picture>
                                            </Link>
                                        ) : (
                                            <picture>
                                                {banner.mobile_image_url && (
                                                    <source media="(max-width: 768px)" srcSet={banner.mobile_image_url} />
                                                )}
                                                <img 
                                                    src={banner.image_url} 
                                                    alt={`Banner ${idx + 1}`} 
                                                    className="w-full h-[360px] sm:h-[440px] md:h-[500px] object-cover" 
                                                />
                                            </picture>
                                        )}

                                        
                                    </div>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </section>
            )}

            {!searchTerm && <BenefitsBar />}

            <main className="flex-grow container mx-auto px-4 py-8" id="catalog-start">
                
                {/* BANNER OFICIAL DE RESTAURANTE & DELIVERY */}
                {!searchTerm && isFoodStyle && (
                    <div className="mb-8 bg-gradient-to-r from-amber-500/10 via-orange-500/15 to-red-500/10 border border-orange-200/80 rounded-2xl p-4 md:p-5 flex flex-wrap items-center justify-between gap-4 animate-fadeIn">
                        <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-700 font-extrabold text-xs">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                ABERTO AGORA
                            </div>
                            <div className="flex items-center gap-2 text-gray-700 font-medium">
                                <FontAwesomeIcon icon={faClock} className="h-4 w-4 text-[var(--primary)]" />
                                Tempo médio: <strong className="text-gray-900 font-bold">25 - 35 min</strong>
                            </div>
                            <div className="flex items-center gap-2 text-gray-700 font-medium">
                                <FontAwesomeIcon icon={faMotorcycle} className="h-4 w-4 text-[var(--primary)]" />
                                Entrega Grátis acima de <strong className="text-gray-900 font-bold">{formatCurrency(50, settings.currency)}</strong>
                            </div>
                        </div>
                        <span className="text-xs font-bold uppercase tracking-wider px-3.5 py-1.5 rounded-full bg-white text-orange-700 border border-orange-200 shadow-xs">
                            Cardápio Oficial & Delivery
                        </span>
                    </div>
                )}

                {/* 1. TOP CATEGORIAS (Bubbles) */}
                {!searchTerm && settings.top_categories_data && settings.top_categories_data.length > 0 && (
                    <section className="mb-10 animate-fadeIn">
                        <div className="flex overflow-x-auto gap-4 md:gap-8 pb-4 scrollbar-hide snap-x justify-start md:justify-center">
                            {settings.top_categories_data.map(cat => (
                                <Link key={cat.id} href={`/buscar?category_slug=${cat.slug}`} className="flex flex-col items-center min-w-[70px] md:min-w-[90px] snap-center group">
                                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden mb-2 group-hover:border-[var(--primary)] group-hover:shadow-md transition-all">
                                        {cat.image_url 
                                            ? <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" /> 
                                            : <span className="text-gray-400 font-bold text-xl uppercase">{cat.name.substring(0, 2)}</span>
                                        }
                                    </div>
                                    <span className="text-xs font-semibold text-gray-700 text-center group-hover:text-[var(--primary)] transition-colors">{cat.name}</span>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Busca Ativa */}
                {searchTerm && (
                    <div className="mb-8">
                        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"><SearchIcon className="w-5 h-5 text-[var(--primary)]"/> Resultados para "{searchTerm}"</h2>
                        {isLoadingProducts ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">{Array.from({ length: 10 }).map((_, i) => <ProductSkeleton key={i} />)}</div>
                        ) : products.length > 0 ? (
                            <div className={isFoodStyle ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6"}>
                                {products.map(product => isFoodStyle ? <FoodProductCard key={product.id} product={product} /> : <ProductCard key={product.id} product={product} />)}
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-white rounded-xl shadow-sm"><p className="text-gray-500 text-lg">Nenhum produto encontrado.</p><button onClick={() => setSearchTerm('')} className="mt-4 text-[var(--primary)] font-bold hover:underline">Limpar busca</button></div>
                        )}
                        {totalPages > 1 && (
                            <div className="flex justify-center mt-10 gap-2">
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="filter-pill filter-pill-inactive disabled:opacity-50">Anterior</button>
                                <span className="px-4 py-2 text-sm text-gray-500">Página {currentPage} de {totalPages}</span>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="filter-pill filter-pill-inactive disabled:opacity-50">Próxima</button>
                            </div>
                        )}
                    </div>
                )}

                {/* Catálogo Padrão */}
                {!searchTerm && !isLoadingData && (
                    <div className="space-y-12 md:space-y-16">

                        {/* DESTAQUES GERAIS */}
                        {!settings.ocultar_produtos && featuredProducts.length > 0 && (
                            <section>
                                <div className="flex items-center justify-between mb-4 md:mb-6">
                                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 border-l-4 border-[var(--primary)] pl-3">Destaques</h2>
                                </div>
                                <Swiper
                                    modules={[Navigation, Autoplay]}
                                    spaceBetween={16}
                                    slidesPerView={isFoodStyle ? 1.2 : 2}
                                    navigation
                                    autoplay={{ delay: 5000 }}
                                    breakpoints={{
                                        640: { slidesPerView: isFoodStyle ? 2 : 3 },
                                        1024: { slidesPerView: isFoodStyle ? 3 : 4 },
                                        1280: { slidesPerView: isFoodStyle ? 4 : 5 },
                                    }}
                                    className="!pb-4"
                                >
                                    {featuredProducts.map(product => (
                                        <SwiperSlide key={product.id}>
                                            {isFoodStyle ? <FoodProductCard product={product} /> : <ProductCard product={product} />}
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </section>
                        )}

                        {/* BANNER PROMOCIONAL SECUNDÁRIO (Faixa Intermediária) */}
                        {settings.banner_url && (
                            <div className="w-full my-8 md:my-10">
                                <div className="overflow-hidden rounded-2xl md:rounded-3xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                    <img 
                                        src={settings.banner_url} 
                                        alt="Banner Promocional Secundário" 
                                        className="w-full h-auto object-cover max-h-[350px]" 
                                    />
                                </div>
                            </div>
                        )}

                        {/* SESSÕES */}
                        {!settings.ocultar_produtos && sections.map(section => (
                            <React.Fragment key={section.id}>
                                {section.banner_url && (
                                    <div className="w-full mb-6 md:mb-8">
                                        {section.banner_action_url ? (
                                            <Link href={section.banner_action_url} className="block w-full transition-transform duration-300 hover:scale-[1.01]">
                                                <img 
                                                    src={section.banner_url} 
                                                    alt={`Banner ${section.name}`} 
                                                    className="w-full h-auto object-cover rounded-2xl" 
                                                />
                                            </Link>
                                        ) : (
                                            <img 
                                                src={section.banner_url} 
                                                alt={`Banner ${section.name}`} 
                                                className="w-full h-auto object-cover rounded-2xl" 
                                            />
                                        )}
                                    </div>
                                )}
                                
                                {section.products && section.products.length > 0 && (
                                    <section>
                                        <div className="flex items-center justify-between mb-4 md:mb-6">
                                            <h2 className="text-xl md:text-2xl font-bold text-gray-900">{section.name}</h2>
                                            <Link href={`/buscar?search=${encodeURIComponent(section.name)}`} className="text-sm font-semibold text-[var(--primary)] hover:underline flex items-center gap-1">
                                                Ver todos <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3" />
                                            </Link>
                                        </div>
                                        {section.display_layout === 'carousel' ? (
                                            <Swiper
                                                modules={[Navigation, Autoplay]}
                                                spaceBetween={16}
                                                slidesPerView={isFoodStyle ? 1.2 : 2}
                                                navigation
                                                autoplay={{ delay: 6000, disableOnInteraction: true }}
                                                breakpoints={{
                                                    640: { slidesPerView: isFoodStyle ? 2 : 3 },
                                                    1024: { slidesPerView: isFoodStyle ? 3 : 4 },
                                                    1280: { slidesPerView: isFoodStyle ? 4 : 5 },
                                                }}
                                                className="!pb-4"
                                            >
                                                {section.products.slice(0, 15).map(product => (
                                                    <SwiperSlide key={product.id}>
                                                        {isFoodStyle ? <FoodProductCard product={product} /> : <ProductCard product={product} />}
                                                    </SwiperSlide>
                                                ))}
                                            </Swiper>
                                        ) : (
                                            <div className={isFoodStyle ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" : "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6"}>
                                                {section.products.slice(0, 10).map(product => (
                                                    isFoodStyle ? <FoodProductCard key={product.id} product={product} /> : <ProductCard key={product.id} product={product} />
                                                ))}
                                            </div>
                                        )}
                                    </section>
                                )}
                            </React.Fragment>
                        ))}

                        {/* FALLBACK RETROCOMPATÍVEL: CATÁLOGO GERAL DE PRODUTOS PARA LOJAS SEM SEÇÕES */}
                        {!settings.ocultar_produtos && sections.length === 0 && products.length > 0 && (
                            <section>
                                <div className="flex items-center justify-between mb-4 md:mb-6">
                                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 border-l-4 border-[var(--primary)] pl-3">
                                        {isFoodStyle ? 'Cardápio Completo' : 'Nosso Catálogo'}
                                    </h2>
                                    <Link href="/buscar" className="text-sm font-semibold text-[var(--primary)] hover:underline flex items-center gap-1">
                                        Ver todos <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3" />
                                    </Link>
                                </div>
                                <div className={isFoodStyle ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" : "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6"}>
                                    {products.slice(0, 20).map(product => (
                                        isFoodStyle ? <FoodProductCard key={product.id} product={product} /> : <ProductCard key={product.id} product={product} />
                                    ))}
                                </div>
                            </section>
                        )}



                        {/* SERVIÇOS & AGENDAMENTOS */}
                        {!settings.ocultar_reservas && services.length > 0 && (
                            <section className="mt-14" id="agendamentos">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <span className="text-xs font-black tracking-widest uppercase text-[var(--primary)]">ATENDIMENTO & HORÁRIOS</span>
                                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 border-l-4 border-[var(--primary)] pl-3 mt-1">Serviços & Agendamentos</h2>
                                    </div>
                                    <button 
                                        onClick={() => setIsBookingModalOpen(true)}
                                        className="inline-flex items-center gap-2 bg-[var(--primary)] text-white text-xs md:text-sm font-bold px-5 py-2.5 rounded-full shadow-sm hover:brightness-110 active:scale-95 transition-all"
                                    >
                                        <FontAwesomeIcon icon={faCalendarAlt} className="h-4 w-4" /> Agendar Horário
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                                    {services.slice(0, 6).map(service => (
                                        <div key={service.id} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:border-[var(--primary)]/30 transition-all flex flex-col justify-between">
                                            <div>
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-gray-100 text-gray-700">
                                                        {service.category?.name || 'Serviço'}
                                                    </span>
                                                    {service.sale_price && (
                                                        <span className="text-base font-bold text-[var(--primary)]">
                                                            {formatCurrency(service.sale_price, settings.currency)}
                                                        </span>
                                                    )}
                                                </div>
                                                <h3 className="font-bold text-gray-900 text-lg mb-2">{service.name}</h3>
                                                {service.description && (
                                                    <p className="text-sm text-gray-500 line-clamp-2 mb-4">{service.description}</p>
                                                )}
                                                {service.duration_minutes && (
                                                    <span className="text-xs text-gray-400 block mb-2 font-medium">Duração estimada: {service.duration_minutes} min</span>
                                                )}
                                            </div>
                                            <button 
                                                onClick={() => setIsBookingModalOpen(true)}
                                                className="mt-4 w-full bg-gray-50 hover:bg-[var(--primary)] text-gray-800 hover:text-white font-bold py-2.5 px-4 rounded-xl text-xs md:text-sm transition-colors border border-gray-200 hover:border-transparent flex items-center justify-center gap-2"
                                            >
                                                <FontAwesomeIcon icon={faCalendarAlt} className="h-3.5 w-3.5" /> Solicitar Agendamento
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {!settings.ocultar_produtos && (sections.length > 0 || products.length > 0) && (
                            <div className="mt-12 text-center">
                                <Link href="/buscar" className="inline-flex items-center gap-3 bg-white border-2 border-gray-200 hover:border-[var(--primary)] hover:text-[var(--primary)] text-gray-700 font-bold py-4 px-10 rounded-full transition-all shadow-sm hover:shadow-lg transform hover:-translate-y-1 text-lg">
                                    <FontAwesomeIcon icon={faThLarge} /> Ver Catálogo Completo
                                </Link>
                            </div>
                        )}


                        {!settings.ocultar_produtos && sections.length === 0 && products.length === 0 && (
                            <div className="text-center py-20"><p className="text-gray-500">Nenhum produto disponível na loja.</p></div>
                        )}
                    </div>
                )}
            </main>

            <Footer />
            <BookingModal isOpen={isBookingModalOpen} onClose={closeBookingModal} />
        </div>
    );
}